import React, { useCallback, useRef, useState, useEffect } from 'react';

export type LogLevel = 'info' | 'success' | 'warn' | 'error';

export interface LogEntry {
  id: number;
  ts: number;
  level: LogLevel;
  message: string;
  detail?: string;
}

export interface DebugLogHandle {
  entries: LogEntry[];
  info: (msg: string, detail?: any) => void;
  success: (msg: string, detail?: any) => void;
  warn: (msg: string, detail?: any) => void;
  error: (msg: string, detail?: any) => void;
  clear: () => void;
}

const fmtDetail = (d: any): string | undefined => {
  if (d === undefined || d === null) return undefined;
  if (d instanceof Error) return d.message;
  if (typeof d === 'string') return d;
  try { return JSON.stringify(d); } catch { return String(d); }
};

export const useDebugLog = (): DebugLogHandle => {
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const idRef = useRef(0);

  const push = useCallback((level: LogLevel, message: string, detail?: any) => {
    setEntries((prev) => [
      ...prev,
      { id: ++idRef.current, ts: Date.now(), level, message, detail: fmtDetail(detail) },
    ].slice(-500));
  }, []);

  return {
    entries,
    info: useCallback((m, d) => push('info', m, d), [push]),
    success: useCallback((m, d) => push('success', m, d), [push]),
    warn: useCallback((m, d) => push('warn', m, d), [push]),
    error: useCallback((m, d) => push('error', m, d), [push]),
    clear: useCallback(() => setEntries([]), []),
  };
};

const colors: Record<LogLevel, string> = {
  info: 'text-gray-300',
  success: 'text-emerald-400',
  warn: 'text-amber-400',
  error: 'text-red-400',
};
const dot: Record<LogLevel, string> = {
  info: 'bg-gray-500',
  success: 'bg-emerald-500',
  warn: 'bg-amber-500',
  error: 'bg-red-500',
};

interface Props {
  log: DebugLogHandle;
  title?: string;
}

export const DebugLog: React.FC<Props> = ({ log, title = 'Debug Log' }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoscroll, setAutoscroll] = useState(true);

  useEffect(() => {
    if (autoscroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [log.entries, autoscroll]);

  const counts = log.entries.reduce(
    (acc, e) => { acc[e.level]++; return acc; },
    { info: 0, success: 0, warn: 0, error: 0 } as Record<LogLevel, number>,
  );

  return (
    <div className="bg-slate-950 text-gray-100 rounded-xl border border-slate-800 flex flex-col" style={{ minHeight: 400, maxHeight: 760 }}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-white">{title}</h3>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="text-emerald-400">✓ {counts.success}</span>
            <span className="text-amber-400">⚠ {counts.warn}</span>
            <span className="text-red-400">✗ {counts.error}</span>
            <span>· {log.entries.length}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoscroll}
              onChange={(e) => setAutoscroll(e.target.checked)}
              className="accent-brand-500"
            />
            Auto-scroll
          </label>
          <button
            onClick={log.clear}
            className="text-xs px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-gray-300"
          >
            Clear
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 font-mono text-xs leading-relaxed">
        {log.entries.length === 0 ? (
          <div className="text-gray-500 italic">No log entries yet.</div>
        ) : (
          log.entries.map((e) => (
            <div key={e.id} className="flex gap-2 mb-0.5">
              <span className="text-gray-600 flex-shrink-0">
                {new Date(e.ts).toLocaleTimeString('en-US', { hour12: false })}
              </span>
              <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${dot[e.level]}`} />
              <span className={`${colors[e.level]} break-words`}>
                {e.message}
                {e.detail ? <span className="text-gray-500"> — {e.detail}</span> : null}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
